// Schalter pricing_enforcement im System-Item FEATURE_FLAGS, aus Sicht des
// Aivy OS. Reine Funktionen ohne React und ohne Netz. Gelesen und geschrieben
// wird über useFeatureFlags (src/hooks/use-feature-flags.js), also mit
// demselben Konfliktschutz über updatedAt wie die Experimente-Seite.
//
// Die Auswertung folgt evaluateFlag im Dashboard
// (src/features/experiments/bucketing.js) in derselben Reihenfolge. Nur die
// md5-Zuteilung bei Teil-Prozenten rechnet das OS nicht nach, das ergibt
// partial. Für diesen Schalter gibt es nur 0 oder 100 % und Overrides
// (Rollout-Regeln in aivy-customer-dashboard/src/features/experiments/README.md).
import { isEqual } from 'lodash'

import {
  KIND,
  STATUS,
  controlOf,
  parseFlagsSystem,
  variantKeys
} from './experiments'
import { STAGE, needsProductionConfirm } from './stage'
import { selectPayments } from './pricing-mirror/payment-selection'

export const PRICING_FLAG_KEY = 'pricing_enforcement'

export const FLAG_STATES = { ON: 'on', OFF: 'off', PARTIAL: 'partial' }

export const ENFORCEMENT_STATES = {
  ACTIVE: 'active',
  READY: 'ready',
  WAITING: 'waiting',
  OFF: 'off',
  PARTIAL: 'partial',
  UNKNOWN: 'unknown',
  // Schema-Probe fehlgeschlagen, die Payments sind ohne plan geladen
  PLAN_UNKNOWN: 'plan_unknown'
}

// Texte für Badge und Unterzeile. Die Labels sagen Schalter und Paket
// zusammen, damit sie neben der Spalte Vertrag nicht doppelt „Altvertrag"
// heißen.
export const ENFORCEMENT_LABELS = {
  active: {
    label: 'Pricing-Sperre aktiv',
    detail: 'Das Paket wird im Dashboard durchgesetzt.'
  },
  ready: {
    label: 'Paket hinterlegt, Sperre aus',
    detail:
      'Das Paket gilt erst, wenn die Sperre für diesen Kunden an ist. Bis dahin rechnet das Dashboard mit dem Altvertrag.'
  },
  waiting: {
    label: 'Sperre an, Paket fehlt',
    detail:
      'Die Sperre greift erst mit einer Paket-Zahlung, bis dahin gilt der Altvertrag.'
  },
  off: {
    label: 'Pricing-Sperre aus',
    detail: 'Kein Paket und keine Sperre, Verhalten wie heute.'
  },
  partial: {
    label: 'Teil-Rollout',
    detail: 'Das OS rechnet die Zuteilung bei Teil-Prozenten nicht nach.'
  },
  unknown: {
    label: 'Flag nicht lesbar',
    detail: 'FEATURE_FLAGS konnte im OS nicht geladen oder gelesen werden.'
  },
  plan_unknown: {
    label: 'Paket nicht lesbar',
    detail:
      'Die Schema-Prüfung ist fehlgeschlagen. Das OS sieht nicht, welche Zahlung ein Paket hat, und kann den Status der Sperre nicht bestimmen.'
  }
}

export const OVERRIDE_VARIANTS = ['on', 'off']

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const matchesList = (allowed, value) =>
  !Array.isArray(allowed) || allowed.length === 0 || allowed.includes(value)

const clampPercent = (value) => Math.min(100, Math.max(0, Number(value) || 0))

// Wie useFlag im Dashboard. Ein Rollout ist an bei on, ein Experiment bei
// jeder Variante außer Control.
const isOnVariant = (flag, variant) =>
  flag.kind === KIND.ROLLOUT ? variant === 'on' : variant !== controlOf(flag)

const stateOf = (flag, variant) =>
  variant !== null && variant !== undefined && isOnVariant(flag, variant)
    ? FLAG_STATES.ON
    : FLAG_STATES.OFF

const result = (state, reason, percent = null) => ({ state, reason, percent })

/**
 * Wertet das Flag für einen Partner aus, mit Begründung. Reihenfolge wie
 * evaluateFlag im Dashboard. Gesetzt sind dort stage, partnerId, leadAmpel
 * (partner.lead_ampel) und isTrial (partner.is_trial). Ohne partner kann das
 * OS Targeting auf lead_ampel nicht prüfen und meldet partial.
 *
 * @param {object|null|undefined} flag Eintrag pricing_enforcement
 * @param {string|null} partnerId
 * @param {{ stage?: string, partner?: object }} [ctx]
 * @returns {{ state: 'on'|'off'|'partial', reason: string,
 *   percent: number|null }}
 */
export const describePricingFlag = (
  flag,
  partnerId,
  { stage = STAGE, partner } = {}
) => {
  if (!isPlainObject(flag)) return result(FLAG_STATES.OFF, 'missing')

  // Das Dashboard teilt LP-Flags nie selbst zu (resolveFlag)
  if (flag.surface === 'lp') return result(FLAG_STATES.OFF, 'surface')

  const keys = variantKeys(flag)

  if (flag.winner && keys.includes(flag.winner)) {
    return result(stateOf(flag, flag.winner), 'winner')
  }
  if (!flag.status || flag.status === STATUS.DRAFT) {
    return result(FLAG_STATES.OFF, 'draft')
  }
  if (flag.status !== STATUS.RUNNING) {
    return result(FLAG_STATES.OFF, flag.status)
  }

  const targeting = isPlainObject(flag.targeting) ? flag.targeting : {}
  if (Array.isArray(targeting.stages) && targeting.stages.length && !stage) {
    // Stage unbekannt (aws-exports ohne bekannten Bucket)
    return result(FLAG_STATES.PARTIAL, 'targeting_unknown')
  }
  if (!matchesList(targeting.stages, stage)) {
    return result(FLAG_STATES.OFF, 'targeting')
  }
  if (!matchesList(targeting.partner_ids, partnerId || null)) {
    return result(FLAG_STATES.OFF, 'targeting')
  }
  if (Array.isArray(targeting.lead_ampel) && targeting.lead_ampel.length) {
    if (!partner) return result(FLAG_STATES.PARTIAL, 'targeting_unknown')
    if (!targeting.lead_ampel.includes(partner.lead_ampel || null)) {
      return result(FLAG_STATES.OFF, 'targeting')
    }
  }
  if (
    typeof targeting.is_trial === 'boolean' &&
    targeting.is_trial !== !!partner?.is_trial
  ) {
    return result(FLAG_STATES.OFF, 'targeting')
  }

  const overrides = isPlainObject(flag.overrides) ? flag.overrides : {}
  const percent = clampPercent(flag.percent)

  // Andere Units teilen je User oder Besucher zu, nicht je Partner
  if (flag.unit !== 'partner') {
    const values = Object.values(overrides).filter((v) => keys.includes(v))
    if (flag.kind === KIND.ROLLOUT && percent === 0 && !values.includes('on')) {
      return result(FLAG_STATES.OFF, 'unit', percent)
    }
    if (
      flag.kind === KIND.ROLLOUT &&
      percent === 100 &&
      !values.includes('off')
    ) {
      return result(FLAG_STATES.ON, 'unit', percent)
    }
    return result(FLAG_STATES.PARTIAL, 'unit', percent)
  }

  if (!partnerId) return result(FLAG_STATES.OFF, 'no_unit')

  const override = overrides[partnerId]
  if (override && keys.includes(override)) {
    return result(stateOf(flag, override), 'override')
  }

  if (flag.kind !== KIND.ROLLOUT) {
    return result(FLAG_STATES.PARTIAL, 'experiment')
  }
  if (percent === 0) return result(FLAG_STATES.OFF, 'rollout', percent)
  if (percent === 100) return result(FLAG_STATES.ON, 'rollout', percent)
  return result(FLAG_STATES.PARTIAL, 'rollout_partial', percent)
}

/**
 * @returns {'on'|'off'|'partial'}
 */
export const evaluatePricingFlag = (flag, partnerId, ctx) =>
  describePricingFlag(flag, partnerId, ctx).state

/**
 * Eintrag pricing_enforcement aus dem geladenen FEATURE_FLAGS-Stand
 * (flagsQuery.data von useFeatureFlags oder ein rohes System-Item).
 * undefined heißt nicht lesbar (nicht geladen, Fehler, kaputtes JSON), null
 * heißt, das Flag fehlt.
 */
export const pricingFlagOf = (flagsSystem) => {
  if (!flagsSystem) return undefined
  const parsed =
    'parseError' in flagsSystem ? flagsSystem : parseFlagsSystem(flagsSystem)
  if (parsed.parseError) return undefined

  const flag = parsed.flags?.[PRICING_FLAG_KEY]
  return isPlainObject(flag) ? flag : null
}

/**
 * Status der Pricing-Sperre für einen Kunden.
 * - unknown, das Flag ist nicht lesbar (flag undefined)
 * - plan_unknown, die Payments sind ohne plan geladen, weil die Schema-Probe
 *   fehlgeschlagen ist (plansKnown false). Ohne plan sähe jede Paket-Zahlung
 *   wie ein Altvertrag aus.
 * - partial, das OS kann die Zuteilung nicht berechnen
 * - active, Sperre an und Paket-Zahlung vorhanden
 * - waiting, Sperre an, aber nur Altvertrag
 * - ready, Paket-Zahlung vorhanden, Sperre aus
 * - off, weder Sperre noch Paket
 *
 * @param {{ flag: object|null|undefined, partnerId: string,
 *   payments: object[], planById?: object, stage?: string,
 *   partner?: object, plansKnown?: boolean }} params planById nur, wenn
 *   payments ohne plan geladen wurden
 * @returns {'active'|'ready'|'waiting'|'off'|'partial'|'unknown'|
 *   'plan_unknown'}
 */
export const pricingEnforcementState = ({
  flag,
  partnerId,
  payments,
  planById = {},
  stage,
  partner,
  plansKnown = true
} = {}) => {
  if (flag === undefined) return ENFORCEMENT_STATES.UNKNOWN
  if (!plansKnown) return ENFORCEMENT_STATES.PLAN_UNKNOWN

  const ctx = { partner, ...(stage !== undefined ? { stage } : {}) }
  const flagState = evaluatePricingFlag(flag, partnerId, ctx)
  if (flagState === FLAG_STATES.PARTIAL) return ENFORCEMENT_STATES.PARTIAL

  const { planPayment } = selectPayments(payments, planById)

  if (flagState === FLAG_STATES.ON) {
    return planPayment ? ENFORCEMENT_STATES.ACTIVE : ENFORCEMENT_STATES.WAITING
  }
  return planPayment ? ENFORCEMENT_STATES.READY : ENFORCEMENT_STATES.OFF
}

/**
 * Setzt oder entfernt den Override eines Partners im Flag
 * pricing_enforcement und gibt die neue Flags-Map zurück. Die Eingabe bleibt
 * unverändert. Gespeichert wird mit saveFlags(base, next) aus
 * useFeatureFlags, also mit Bedingung auf updatedAt.
 *
 * @param {object} flags Flags-Map (base.flags aus parseFlagsSystem)
 * @param {string} partnerId
 * @param {'on'|'off'|null} variant null entfernt den Override
 * @returns {object}
 */
export const setPartnerOverride = (flags, partnerId, variant) => {
  if (!partnerId || typeof partnerId !== 'string') {
    throw new Error('Partner-ID fehlt')
  }
  if (variant !== null && !OVERRIDE_VARIANTS.includes(variant)) {
    throw new Error(`Unbekannte Variante ${variant}`)
  }

  const flag = isPlainObject(flags) ? flags[PRICING_FLAG_KEY] : null
  if (!isPlainObject(flag)) {
    throw new Error(
      'Flag pricing_enforcement fehlt in FEATURE_FLAGS, bitte zuerst anlegen'
    )
  }

  const overrides = { ...(isPlainObject(flag.overrides) ? flag.overrides : {}) }
  if (variant === null) delete overrides[partnerId]
  else overrides[partnerId] = variant

  return { ...flags, [PRICING_FLAG_KEY]: { ...flag, overrides } }
}

/**
 * Hinweise, warum ein Override nicht wirkt. Leere Liste heißt in Ordnung.
 */
export const pricingFlagWarnings = (flag) => {
  if (!isPlainObject(flag)) return ['Flag pricing_enforcement fehlt']

  const warnings = []
  if (flag.kind !== KIND.ROLLOUT) warnings.push('Flag ist kein Rollout')
  if (flag.unit !== 'partner') warnings.push('Unit ist nicht partner')
  if (flag.surface === 'lp') {
    warnings.push('Surface lp wird im Dashboard nie zugeteilt')
  }
  if (!flag.status || flag.status === STATUS.DRAFT) {
    warnings.push('Status Entwurf, Overrides greifen erst bei running')
  } else if (flag.status !== STATUS.RUNNING) {
    warnings.push('Status nicht running, alle Partner sind aus')
  }
  if (flag.winner) {
    warnings.push('Gewinner gesetzt, Overrides greifen nicht mehr')
  }
  const targeting = isPlainObject(flag.targeting) ? flag.targeting : {}
  if (Array.isArray(targeting.partner_ids) && targeting.partner_ids.length) {
    warnings.push(
      'targeting.partner_ids ist gesetzt und wird vor den Overrides geprüft'
    )
  }
  const percent = clampPercent(flag.percent)
  if (percent > 0 && percent < 100) {
    warnings.push(
      `Teil-Rollout mit ${percent} %, das OS rechnet ihn nicht nach`
    )
  }
  return warnings
}

/**
 * Vorlage für das Flag (Rollout-Regeln in der README der Experimente im
 * Dashboard). Läuft mit 0 %, wirkt also nur über Overrides.
 */
export const buildPricingEnforcementFlag = ({
  owner = '',
  now = new Date()
} = {}) => ({
  key: PRICING_FLAG_KEY,
  description:
    'Pricing-Sperre für Kunden mit Payment.plan. Aus heißt Verhalten wie heute.',
  owner,
  kind: KIND.ROLLOUT,
  status: STATUS.RUNNING,
  unit: 'partner',
  surface: 'dashboard',
  salt: PRICING_FLAG_KEY,
  percent: 0,
  targeting: {},
  overrides: {},
  winner: null,
  started_at: now.toISOString(),
  concluded_at: null
})

// ---------------------------------------------------------------------------
// Schreiben auf production
// ---------------------------------------------------------------------------

export const PRODUCTION_STAGE = 'production'

export class PricingWriteCancelledError extends Error {
  constructor() {
    super('Nicht gespeichert, die Bestätigung für production fehlt.')
    this.name = 'PricingWriteCancelledError'
  }
}

/**
 * Ob ein Schreibvorgang das Flag pricing_enforcement ändert, anlegt oder
 * entfernt. Andere Flags im selben Item zählen nicht.
 *
 * @param {object} base geladener Stand (parseFlagsSystem)
 * @param {object} nextFlags neue Flags-Map
 */
export const touchesPricingFlag = (base, nextFlags) =>
  !isEqual(base?.flags?.[PRICING_FLAG_KEY], nextFlags?.[PRICING_FLAG_KEY])

const defaultConfirm = (message) =>
  typeof window !== 'undefined' && typeof window.confirm === 'function'
    ? window.confirm(message)
    : false

const defaultPrompt = (message) =>
  typeof window !== 'undefined' && typeof window.prompt === 'function'
    ? window.prompt(message)
    : null

/**
 * Doppelte Bestätigung wie im Override-Control der Kundenseite. Erst ein
 * Hinweis, dann muss production eingetippt werden.
 *
 * @returns {boolean} true nur bei beiden Schritten
 */
export const confirmProductionPricingWrite = ({
  confirm = defaultConfirm,
  prompt = defaultPrompt
} = {}) => {
  const first = confirm(
    `Du änderst ${PRICING_FLAG_KEY} auf production. Das schaltet die Pricing-Sperre für echte Kunden um, das Dashboard übernimmt es beim nächsten Laden. Weiter?`
  )
  if (!first) return false
  const typed = prompt(`Gib ${PRODUCTION_STAGE} ein, um zu bestätigen.`)
  return typeof typed === 'string' && typed.trim() === PRODUCTION_STAGE
}

/**
 * Hülle um saveFlags aus useFeatureFlags für die Experimente-Seiten. Ändert
 * ein Schreibvorgang auf production das Flag pricing_enforcement, braucht er
 * die doppelte Bestätigung. Ohne sie wirft die Hülle
 * PricingWriteCancelledError und schreibt nichts.
 *
 * @param {(base: object, next: object) => Promise<object>} saveFlags
 * @param {{ stage?: string, confirmWrite?: () => boolean }} [options]
 */
export const guardPricingFlagSave =
  (
    saveFlags,
    { stage = STAGE, confirmWrite = confirmProductionPricingWrite } = {}
  ) =>
  async (base, nextFlags) => {
    if (
      needsProductionConfirm(stage) &&
      touchesPricingFlag(base, nextFlags) &&
      !confirmWrite()
    ) {
      throw new PricingWriteCancelledError()
    }
    return saveFlags(base, nextFlags)
  }
